import { z } from 'zod';
import {
  AssetLifecycleDtoSchema,
  LifecycleStateFilterSchema,
  PaginationDefaults,
  PaginationQueryParamsSchema,
  BooleanQueryParamDefaultFalseSchema,
  OptionalBooleanQueryParamSchema,
  createConfidenceFilterQueryParamSchema,
} from '@terra/shared-dto-types';
import { TracePreviewBaseSchema } from '@terra/shared-contracts-trace';
import {
  AiComponentTypeSchema,
  BlockedReasonSchema,
  ConfirmedVulnerabilityFindingStateSchema,
  JsonValueSchema,
  SignalCategorySchema,
  SignalOriginSchema,
  SignalSchema,
  SignalStageSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import { SignalLinkedFindingPreviewSchema } from '@terra/shared-contracts-findings';
import { BaseAppSignalDtoSchema } from './signal-base.schema';
import { SignalUpdateInputSchema } from './signals-update.schema';
import { SignalSourceFilterValueSchema } from './signal-source.util';
import {
  SignalListGroupByFieldSchema,
  SignalListGroupCountSchema,
} from './signal-list-sort.schema';

const SIGNAL_CONFIDENCE_FILTER_VALUES = [
  'HIGHEST',
  'HIGH',
  'MEDIUM',
  'LOW',
  'LOWEST',
  'ALL',
] as const;

const SignalConfidenceFilterSchema = createConfidenceFilterQueryParamSchema(
  SIGNAL_CONFIDENCE_FILTER_VALUES,
);

export const SignalsApplicationIdParamsSchema = z.object({
  applicationId: z.string(),
});

export const SignalsCountByApplicationIdParamsSchema = z.object({
  applicationId: z.string(),
  targetId: z.string().optional(),
});

export const SignalWithHarEntryRequestSchema = SignalSchema.extend({
  harEntry: z
    .object({
      request: JsonValueSchema,
    })
    .nullable(),
});

export const SignalsSearchQuerySchema = PaginationQueryParamsSchema.extend({
  stage: SignalStageSchema.array().optional(),
  status: SignalStatusSchema.array().optional(),
  blockedReason: BlockedReasonSchema.array().optional(),
  confirmedVulnerabilityFindingState:
    ConfirmedVulnerabilityFindingStateSchema.array().optional(),
  description: z.string().optional(),
  entryPointId: z.string().optional(),
  securityTestCaseId: z.string().optional(),
  confidence: SignalConfidenceFilterSchema,
  category: SignalCategorySchema.array().optional(),
  cweIds: z.array(z.string()).optional(),
  owaspCategory: z.array(z.string()).optional(),
  origin: SignalOriginSchema.array().optional(),
  source: SignalSourceFilterValueSchema.array().optional(),
  sourceLabel: z.array(z.string()).optional(),
  importBatchId: z.array(z.string()).optional(),
  url: z.string().optional(),
  assigneeId: z.union([z.string(), z.array(z.string())]).optional(),
  endpointId: z.string().optional(),
  endpointIds: z
    .array(z.string())
    .optional()
    .describe('Filter by specific endpoint IDs (for domain tree filtering)'),
  assetIds: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((value) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    )
    .describe(
      'Filter by AI component asset IDs (for AI application component tree filtering)',
    ),
  componentTypes: z
    .union([AiComponentTypeSchema, z.array(AiComponentTypeSchema)])
    .optional()
    .transform((value) =>
      value === undefined ? undefined : Array.isArray(value) ? value : [value],
    )
    .describe(
      'Filter by AI component types (resolved server-side to assetIds; prefer over large assetIds lists)',
    ),
  urlPatterns: z
    .array(z.string())
    .optional()
    .describe('URL patterns for hierarchical filtering (supports regex)'),
  hasExecution: OptionalBooleanQueryParamSchema,
  hasFinding: OptionalBooleanQueryParamSchema,
  hasSecurityTestCase: OptionalBooleanQueryParamSchema,
  isAppWide: OptionalBooleanQueryParamSchema.describe(
    'When true, return only app-wide signals. When false, return only signals scoped to an endpoint or asset.',
  ),
  createdStartDate: z.coerce.date().optional(),
  createdEndDate: z.coerce.date().optional(),
  testedStartDate: z.coerce.date().optional(),
  testedEndDate: z.coerce.date().optional(),
  includeRejected: BooleanQueryParamDefaultFalseSchema.describe(
    'Include rejected signals (validationRejected=true). Default false = exclude from signal queries. Use true only for asset context.',
  ),
  rejectedOnly: OptionalBooleanQueryParamSchema.describe(
    'When true, return only validation-rejected signals (validationRejected=true). If set, includeRejected is ignored for the non-rejected exclusion clause.',
  ),
  includeApplicationLevelGroup: BooleanQueryParamDefaultFalseSchema.describe(
    'When true, include a group for signals with no endpoint or isAppWide=true (application-level). When used with endpointIds/urlPatterns, returns union of endpoint groups and application-level group. With skip/limit pagination, that application-level group is only included when skip is 0 (first page).',
  ),
  lifecycleState: LifecycleStateFilterSchema.optional().describe(
    'Filter by asset lifecycle state (active, archived, or all)',
  ),
});

export const SignalsSearchByApplicationBodySchema =
  SignalsSearchQuerySchema.extend({
    groupBy: SignalListGroupByFieldSchema.optional().describe(
      'When set, every page also returns per-group counts in the requested group order; orderBy[0] should sort by the same field',
    ),
  })
    .refine((data) => data.skip === undefined || data.skip >= 0, {
      path: ['skip'],
      error: 'Skip must be non-negative',
    })
    .refine(
      (data) => {
        const limit = data.limit ?? PaginationDefaults.LIMIT;

        return limit >= 1 && limit <= PaginationDefaults.MAX_LIMIT;
      },
      { path: ['limit'], error: 'Limit must be between 1 and 1000' },
    );

export const SignalsCountByApplicationBodySchema =
  SignalsSearchQuerySchema.omit({
    skip: true,
    limit: true,
    orderBy: true,
  });

export const GroupedSignalsSearchBodySchema = SignalsSearchQuerySchema.extend({
  signalsPerGroup: z.coerce
    .number()
    .optional()
    .describe('Maximum signals per endpoint group (for performance)'),
  allSignalsPerGroup: z
    .union([z.literal('true'), z.literal('false'), z.boolean()])
    .optional()
    .transform((val) => val === true || val === 'true')
    .describe('If true, fetch all signals per group without limit'),
});

export const SIGNAL_GROUP_PAGE_DEFAULT_LIMIT = 100;
export const SIGNAL_GROUP_PAGE_MAX_LIMIT = 200;

export const SignalGroupBySchema = z.enum(['endpoint', 'asset']);

const SignalGroupSummaryEndpointSchema = z
  .object({
    id: z.string(),
    url: z.string(),
    graphqlOperationName: z.string().nullable().optional(),
    mcpOperationName: z.string().nullable().optional(),
    request: z.object({
      method: z.string(),
    }),
  })
  .nullable();

export const SignalGroupSummarySchema = z.object({
  groupKey: z.string().nullable(),
  endpointUrl: z.string().nullable(),
  endpoint: SignalGroupSummaryEndpointSchema,
  signalCount: z.number(),
});

export const SignalGroupsSummaryResponseSchema = z.object({
  groups: z.array(SignalGroupSummarySchema),
  totalSignals: z.number(),
  totalGroups: z.number(),
});

export const SignalGroupsSearchBodySchema = SignalsSearchQuerySchema.extend({
  groupBy: SignalGroupBySchema.optional(),
})
  .refine((data) => data.skip === undefined || data.skip >= 0, {
    path: ['skip'],
    error: 'Skip must be non-negative',
  })
  .refine(
    (data) => {
      const limit = data.limit ?? PaginationDefaults.LIMIT;

      return limit >= 1 && limit <= PaginationDefaults.MAX_LIMIT;
    },
    { path: ['limit'], error: 'Limit must be between 1 and 1000' },
  );

export const SignalGroupPageSearchBodySchema = SignalsSearchQuerySchema.extend({
  groupBy: SignalGroupBySchema.optional(),
  groupKey: z.string().nullable(),
})
  .refine((data) => data.skip === undefined || data.skip >= 0, {
    path: ['skip'],
    error: 'Skip must be non-negative',
  })
  .refine(
    (data) => {
      const limit = data.limit ?? SIGNAL_GROUP_PAGE_DEFAULT_LIMIT;

      return limit >= 1 && limit <= SIGNAL_GROUP_PAGE_MAX_LIMIT;
    },
    {
      path: ['limit'],
      error: `Limit must be between 1 and ${SIGNAL_GROUP_PAGE_MAX_LIMIT}`,
    },
  );

export const SignalsFunnelStageCountsSchema = z.object({
  discovered: z.number(),
  analyzed: z.number(),
  flagged: z.number(),
  confirmed: z.number(),
});

export const SignalsCountByApplicationIdReturnSchema = z.object({
  total: z.number(),
  counts: z.object({
    ALL: z.number(),
    NEW: z.number(),
    IN_TESTING: z.number(),
    NEEDS_REVIEW: z.number(),
    TESTED: z.number(),
  }),
  statusCounts: z.object({
    UNCLASSIFIED: z.number(),
    POTENTIAL_VULNERABILITY: z.number(),
    BLOCKED: z.number(),
    CONFIRMED_VULNERABILITY: z.number(),
    DISPROVEN_VULNERABILITY: z.number(),
    DUPLICATE: z.number(),
  }),
  funnelStageCounts: SignalsFunnelStageCountsSchema,
});

export const SignalsCountByApplicationItemSchema = z.object({
  applicationId: z.string(),
  total: z.number().int().nonnegative(),
  byStage: z.object({
    NEW: z.number().int().nonnegative(),
    IN_TESTING: z.number().int().nonnegative(),
    NEEDS_REVIEW: z.number().int().nonnegative(),
    TESTED: z.number().int().nonnegative(),
  }),
});

export const SignalsCountByApplicationReturnSchema = z.object({
  applications: z.array(SignalsCountByApplicationItemSchema),
});

export const SignalsCountReturnSchema = z.object({
  count: z.number(),
});

export const SignalsAggregatedCountReturnSchema = z.object({
  count: z.number(),
});

export const SignalQualityDistributionSchema = z.object({
  new: z.number(),
  inTesting: z.number(),
  needsReview: z.number(),
  tested: z.number(),
  potentialVulnerability: z.number(),
  blocked: z.number(),
  confirmedVulnerability: z.number(),
  disprovenVulnerability: z.number(),
  duplicate: z.number(),
});

export const SignalCategoryDistributionSchema = z.record(
  z.string(),
  z.number(),
);

export const TopEndpointSignalCategorySchema = z.object({
  category: SignalCategorySchema,
  amount: z.number(),
});

export const TopEndpointBySignalsSchema = z.object({
  endpointId: z.string(),
  path: z.string(),
  signalCategories: z.array(TopEndpointSignalCategorySchema),
});

export const TopEndpointsBySignalsSchema = z.array(TopEndpointBySignalsSchema);

export const TopEndpointsBySignalsQuerySchema = z.object({
  from: z.string().optional().describe('Start date filter (ISO string)'),
  to: z.string().optional().describe('End date filter (ISO string)'),
  environment: z.string().optional().describe('Filter by environment'),
  businessUnitId: z.string().optional().describe('Filter by business unit ID'),
  applicationId: z.string().optional().describe('Filter by application ID'),
  targetId: z.string().optional().describe('Alias for application ID'),
  labels: SignalStageSchema.array()
    .optional()
    .describe('Deprecated alias for stage filter'),
  stages: SignalStageSchema.array()
    .optional()
    .describe('Filter by signal stages'),
  statuses: SignalStatusSchema.array()
    .optional()
    .describe('Filter by signal statuses'),
  confirmedVulnerabilityFindingState:
    ConfirmedVulnerabilityFindingStateSchema.array()
      .optional()
      .describe('Filter confirmed vulnerability signals by finding state'),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .optional()
    .describe('Top endpoints limit'),
});

export const SignalTrendsOverTimeSchema = z.object({
  new: z.array(z.number()),
  inTesting: z.array(z.number()),
  needsReview: z.array(z.number()),
  tested: z.array(z.number()),
  months: z.array(z.string()),
});

const GroupedSignalEndpointSchema = z
  .object({
    id: z.string(),
    url: z.string(),
    graphqlOperationName: z.string().nullable().optional(),
    mcpOperationName: z.string().nullable().optional(),
    request: z.object({
      method: z.string(),
    }),
  })
  .nullable();

export const SignalRelatedAssetKindSchema = z.enum(['endpoint', 'aiComponent']);

export const SignalRelatedAssetSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: SignalRelatedAssetKindSchema,
  type: z.string(),
  lifecycle: AssetLifecycleDtoSchema.optional(),
});

export type SignalRelatedAssetDto = z.infer<typeof SignalRelatedAssetSchema>;

export {
  SignalLinkedFindingPreviewSchema,
  type SignalLinkedFindingPreviewDto,
} from '@terra/shared-contracts-findings';

const GroupedSignalBaseFieldsSchema = BaseAppSignalDtoSchema.omit({
  origin: true,
}).extend({
  endpoint: GroupedSignalEndpointSchema,
  traces: z.array(TracePreviewBaseSchema).optional(),
  generationTraces: z.array(TracePreviewBaseSchema).optional(),
  relatedAssets: z.array(SignalRelatedAssetSchema).optional(),
  targetSummary: z.string().nullable().optional(),
  linkedFinding: SignalLinkedFindingPreviewSchema.nullish(),
});

/**
 * Chaining-specific variant of `GroupedSignal`. Adds a typed list of the
 * finding IDs that composed the chain (persisted on the row under
 * `metadata.chainingConstituentFindingIds` as a stringified array). Any other
 * origin will hit the non-chaining branch below and won't carry this field.
 */
export const ChainingGroupedSignalSchema = GroupedSignalBaseFieldsSchema.extend(
  {
    origin: z.literal(SignalOriginSchema.enum.FINDING_CHAINING),
    constituentFindingIds: z.array(z.string()),
  },
);

export const NonChainingGroupedSignalSchema =
  GroupedSignalBaseFieldsSchema.extend({
    origin: SignalOriginSchema.exclude([
      SignalOriginSchema.enum.FINDING_CHAINING,
    ]),
  });

export const GroupedSignalSchema = z.discriminatedUnion('origin', [
  ChainingGroupedSignalSchema,
  NonChainingGroupedSignalSchema,
]);

export type ChainingGroupedSignal = z.infer<typeof ChainingGroupedSignalSchema>;
export type NonChainingGroupedSignal = z.infer<
  typeof NonChainingGroupedSignalSchema
>;
export type GroupedSignal = z.infer<typeof GroupedSignalSchema>;

export const SignalGroupPageResponseSchema = z.object({
  signals: z.array(GroupedSignalSchema),
  hasMore: z.boolean(),
});

export const GroupedSignalsByEndpointSchema = z.object({
  endpointId: z.string().nullable(),
  endpointUrl: z.string().nullable(),
  signals: z.array(GroupedSignalSchema),
  signalCount: z.number(),
});

export const GroupedSignalsResponseSchema = z.object({
  groups: z.array(GroupedSignalsByEndpointSchema),
  totalSignals: z.number(),
  totalGroups: z.number(),
});

export const SignalsFlatResponseSchema = z.object({
  signals: z.array(GroupedSignalSchema),
  totalCount: z.number(),
  groups: z.array(SignalListGroupCountSchema).optional(),
});

export type GroupedSignalsByEndpoint = z.infer<
  typeof GroupedSignalsByEndpointSchema
>;
export type GroupedSignalsResponse = z.infer<
  typeof GroupedSignalsResponseSchema
>;
export type SignalGroupSummary = z.infer<typeof SignalGroupSummarySchema>;
export type SignalGroupsSummaryResponse = z.infer<
  typeof SignalGroupsSummaryResponseSchema
>;
export type SignalGroupPageResponse = z.infer<
  typeof SignalGroupPageResponseSchema
>;
export type SignalGroupBy = z.infer<typeof SignalGroupBySchema>;
export type SignalsFlatResponse = z.infer<typeof SignalsFlatResponseSchema>;

export const BulkUpdateSignalsPayloadSchema = z.object({
  updates: z.array(
    z.object({
      id: z.string(),
      update: SignalUpdateInputSchema,
    }),
  ),
});

export const BulkUpdateSignalsResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  updatedCount: z.number(),
  updatedIds: z.array(z.string()),
});

export const BulkDeleteSignalsPayloadSchema = z.object({
  ids: z.array(z.string()),
});

export const BulkDeleteSignalsResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  deletedIds: z.array(z.string()),
});

export const EndpointSignalCountSchema = z.object({
  endpointId: z.string().nullable(),
  endpointUrl: z.string().nullable(),
  signalCount: z.number(),
});

export const SignalsCountByEndpointReturnSchema = z.object({
  endpointCounts: z.array(EndpointSignalCountSchema),
  totalSignals: z.number(),
});

export type EndpointSignalCount = z.infer<typeof EndpointSignalCountSchema>;
export type SignalsCountByEndpointReturn = z.infer<
  typeof SignalsCountByEndpointReturnSchema
>;

export const AssetSignalCountSchema = z.object({
  assetId: z.string().nullable(),
  signalCount: z.number(),
});

export const SignalsCountByAssetReturnSchema = z.object({
  assetCounts: z.array(AssetSignalCountSchema),
  totalSignals: z.number(),
});

export const SignalsCountByAssetQuerySchema = SignalsSearchQuerySchema.omit({
  skip: true,
  limit: true,
  orderBy: true,
  assetIds: true,
  componentTypes: true,
  urlPatterns: true,
  includeApplicationLevelGroup: true,
});

export type AssetSignalCount = z.infer<typeof AssetSignalCountSchema>;
export type SignalsCountByAssetReturn = z.infer<
  typeof SignalsCountByAssetReturnSchema
>;
export type SignalsCountByAssetQuery = z.infer<
  typeof SignalsCountByAssetQuerySchema
>;

export const SignalsCountByStageQuerySchema = z.object({
  from: z.coerce.date().optional().describe('Start date filter'),
  to: z.coerce.date().optional().describe('End date filter'),
  environment: z.string().optional().describe('Filter by environment'),
  businessUnitId: z.string().optional().describe('Filter by business unit ID'),
});

export const SignalsCountByStatusQuerySchema = SignalsCountByStageQuerySchema;

export const SignalsCountByStageResponseSchema = z.object({
  total: z.number(),
  byStage: z.object({
    NEW: z.number(),
    IN_TESTING: z.number(),
    NEEDS_REVIEW: z.number(),
    TESTED: z.number(),
  }),
});

export const FindingsCountBySeveritySchema = z.object({
  Critical: z.number(),
  High: z.number(),
  Medium: z.number(),
  Low: z.number(),
  Info: z.number(),
});

export const ConfirmedVulnerabilitySignalsCountSchema = z.object({
  count: z.number(),
  findingsBySeverity: FindingsCountBySeveritySchema,
});

export const SignalsCountByStatusResponseSchema = z.object({
  total: z.number(),
  byStatus: z.object({
    UNCLASSIFIED: z.number(),
    POTENTIAL_VULNERABILITY: z.number(),
    BLOCKED: z.number(),
    CONFIRMED_VULNERABILITY: z.number(),
    DISPROVEN_VULNERABILITY: z.number(),
    DUPLICATE: z.number(),
  }),
  confirmedVulnerabilitySignals: ConfirmedVulnerabilitySignalsCountSchema,
});

export type FindingsCountBySeverity = z.infer<
  typeof FindingsCountBySeveritySchema
>;
export type SignalsCountByStatusResponse = z.infer<
  typeof SignalsCountByStatusResponseSchema
>;
export type SignalsCountByStageResponse = z.infer<
  typeof SignalsCountByStageResponseSchema
>;
